import { AGENT_ID, apiPath } from "./base";

export interface SurfaceResources { ui: string; media: string; mediaOrigin?: string }
export interface SurfacePage { html: string; resources?: SurfaceResources }

/** Accept only the current Hub's exact agent/panel resource mount. */
export function surfaceResources(headers: Headers, panelKey: string): SurfaceResources | undefined {
  const ui = headers.get("x-pa-surface-ui");
  const media = headers.get("x-pa-surface-media");
  if (ui === null && media === null) return undefined;
  const refuse = () => new Error("界面资源地址不属于当前 IP 的面板，请重新加载界面。");
  if (!AGENT_ID || !ui || !media) throw refuse();
  let first: URL, second: URL;
  try { first = new URL(ui, location.origin); second = new URL(media, location.origin); }
  catch { throw refuse(); }
  const capability = first.pathname.split("/")[2] || "";
  const base = `/surface-assets/${capability}${apiPath(`/api/panels/${encodeURIComponent(panelKey)}/`)}`;
  if (!/^[A-Za-z0-9_-]+\.[a-f0-9]{64}$/.test(capability)
    || first.origin !== location.origin || second.origin !== location.origin
    || first.username || first.password || second.username || second.password
    || first.search || first.hash || second.search || second.hash
    || first.pathname !== base + "ui/" || second.pathname !== base + "media/") throw refuse();
  const origin = mediaOrigin(headers);
  return { ui: first.href, media: second.href, ...(origin ? { mediaOrigin: origin } : {}) };
}

/** Only the hosting server can add this header. It grants media, never scripts. */
export function mediaOrigin(headers: Headers): string | undefined {
  const raw = headers.get("x-pa-media-origin");
  if (raw === null || raw === "") return undefined;
  return checkedMediaOrigin(raw);
}

export function checkedMediaOrigin(raw: string): string {
  let parsed: URL;
  try { parsed = new URL(raw); }
  catch { throw new Error("媒体分发地址无效。"); }
  if (parsed.protocol !== "https:" || parsed.origin !== raw || parsed.username || parsed.password)
    throw new Error("媒体分发地址无效。");
  return parsed.origin;
}

/** Diagnostics describe files; scoped access values must not reach the model. */
export function surfaceDiagnostic(value: unknown): unknown {
  if (typeof value === "string") return value
    .replace(/https?:\/\/[^\s"<>]+[?&](?:x-oss-signature|OSSAccessKeyId|X-Amz-Signature)=[^\s"<>]*/gi, "[signed media URL]")
    .replace(/(?:https?:\/\/[^\s/]+)?\/surface-assets\/[^/\s]+\/agents\/[^/\s]+\/api\/panels\/[^/\s]+\/(ui|media)\//g, "$1/")
    .replace(/(\/surface-assets\/)[^/\s]+/g, "$1[redacted]");
  if (Array.isArray(value)) return value.map(surfaceDiagnostic);
  if (value && typeof value === "object") return Object.fromEntries(Object.entries(value).map(([key, item]) => [key, surfaceDiagnostic(item)]));
  return value;
}
